import React from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { FormSpy } from 'react-final-form';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { useAttestationsListQuery } from 'src/api';
import { Button, FullscreenLoader } from 'src/components/common';
import {
  CheckboxField,
  RadioGroupField,
  RadioGroupOption,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { AttestationPurpose } from 'src/constants/attestation';
import { useAuthSignup } from 'src/hooks/auth';
import { useVerifierSignupPermissiblePurposeReferenceOptions } from 'src/hooks/permissiblePurposeReference';
import type { SxProp } from 'src/types/mui';
import { shouldDisplayError } from 'src/utils/shouldDisplayError';

import { RenderErrorMessage } from '../../RenderErrorMessage';
import { VerifierAccountTypeFormFields, VerifierAccountTypeFormValues } from '../types';
import { verifierAccountTypeFormValidationSchema } from '../utils';

export const VerifierAccountTypeForm: React.FC = () => {
  const { updateFormData, goToNextStep } = useAuthSignup();

  const { data, loading } = useAttestationsListQuery();

  const {
    onFormChange,
    permissiblePurposeReferencesOptionList,
    permissiblePurposeReferencesLoading,
  } = useVerifierSignupPermissiblePurposeReferenceOptions();

  const attestations = data?.attestationList.filter(attestation =>
    attestation.purpose?.includes(AttestationPurpose.forVerifiers),
  );

  const handleSubmit = React.useCallback(
    (formValues: VerifierAccountTypeFormValues) => {
      updateFormData(formValues);
      goToNextStep();
    },
    [goToNextStep, updateFormData],
  );

  const renderFormContent = React.useCallback(() => {
    if (loading) {
      return <FullscreenLoader />;
    }

    return (
      <Form<VerifierAccountTypeFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{
          valid: true,
          values: true,
          submitting: true,
          errors: true,
          dirtyFields: true,
        }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(verifierAccountTypeFormValidationSchema(attestations))}
      >
        {({ submitting, valid, errors, dirtyFields, form }) => (
          <React.Fragment>
            <Box mb={3}>
              <Field
                component={RadioGroupField}
                name={VerifierAccountTypeFormFields.accountType}
                options={accountTypeOptions}
                fullWidth
                required
              />
              <Typography variant="body1" color="text.secondary" my="24px">
                To create Verification requests on the platform you need to select a Permissible
                Purpose that you company is certified for. If you need to change it or select more
                than one, you can create a request for it in My account -- Company information
                section.
              </Typography>
              <FormSpy<VerifierAccountTypeFormValues>
                subscription={{ values: true }}
                onChange={formValues => onFormChange(formValues, form)}
              />
              <Grid mt={2} size={12}>
                <Field
                  component={SelectField}
                  name={VerifierAccountTypeFormFields.permissiblePurposeReferenceId}
                  label="Select Permissible Purpose"
                  loading={permissiblePurposeReferencesLoading}
                  disabled={permissiblePurposeReferencesLoading}
                  options={permissiblePurposeReferencesOptionList}
                  fullWidth
                  required
                />
              </Grid>
            </Box>
            <Grid
              sx={{
                pt: '15px',
                maxHeight: '341px',
                overflow: 'auto',
              }}
              size={12}
            >
              {attestations?.map(fields => (
                <React.Fragment key={fields.id}>
                  <Field
                    fullWidth
                    name={fields.name}
                    multiline
                    label={fields.name}
                    component={TextField}
                    type="text"
                    disabled
                    showErrorWhenDisabled={false}
                    sx={{
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                    slotProps={{
                      input: {
                        value: fields.text,
                        readOnly: true,
                        rows: 0,
                        sx: {
                          '&.Mui-disabled': {
                            textarea: {
                              '-webkit-text-fill-color': '#000000',
                            },
                          },
                        },
                      },
                    }}
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mt={3}
                    mb={shouldDisplayError(fields.name, errors, dirtyFields) ? 0 : 3}
                  >
                    <Field
                      component={CheckboxField}
                      name={fields.name}
                      label={`I have read and agreed to ${fields.name}`}
                      fullWidth
                      required
                    />
                    <Button
                      variant="contained"
                      type="button"
                      component="a"
                      href={fields.pdf?.downloadUrl as string}
                      target="_blank"
                      size="small"
                      sx={{ whiteSpace: 'nowrap' }}
                      color="primary"
                    >
                      See more
                    </Button>
                  </Box>
                  {shouldDisplayError(fields.name, errors, dirtyFields) && (
                    <RenderErrorMessage errors={errors} field={fields.name} />
                  )}
                </React.Fragment>
              ))}
            </Grid>
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                type="submit"
                sx={submitButtonStyles}
                disabled={submitting || !valid}
                loading={submitting}
                size="large"
                variant="contained"
              >
                Next
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    );
  }, [
    attestations,
    handleSubmit,
    loading,
    onFormChange,
    permissiblePurposeReferencesLoading,
    permissiblePurposeReferencesOptionList,
  ]);

  return (
    <Box>
      <Typography variant="h5" fontSize="24px">
        Provide us with your company information
      </Typography>

      <Typography variant="body1" color="text.secondary" mt={1}>
        Choose the type of your account
      </Typography>
      {renderFormContent()}
    </Box>
  );
};

const initialValues: Partial<VerifierAccountTypeFormValues> = {
  accountType: 'Commercial',
};

const submitButtonStyles: SxProp = {
  px: 5,
};

const accountTypeOptions: RadioGroupOption[] = [
  {
    label: (
      <React.Fragment>
        This account is for <b>commercial</b> use
      </React.Fragment>
    ),
    value: 'Commercial',
  },
  {
    label: (
      <React.Fragment>
        This account is for <b>governmental</b> use
      </React.Fragment>
    ),
    value: 'Government',
  },
];
